import React, { useMemo, useState } from "react";
import { CaretLeft, CaretRight, CheckCircle, Clock } from "@phosphor-icons/react";

/**
 * Iter208.16 — Calendario stile Google Calendar per la pagina pubblica di
 * prenotazione appuntamento (`/book-appointment/:token`).
 *
 * Props:
 *  - slots: array di { id, start_at, duration_min }
 *  - selected: Set<slotId> — slot selezionati dal partecipante
 *  - onToggle(slotId)
 *  - disabled: bool — blocca interazione (appuntamento già chiuso o "non disponibile")
 */
const WD_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const WD_MIN   = ["L", "M", "M", "G", "V", "S", "D"];
const MONTHS_IT = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function dayKey(d) { return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; }

function buildMonthGrid(anchor) {
  const first = startOfMonth(anchor);
  const isoDow = (first.getDay() + 6) % 7;
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - isoDow);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
}

export default function PublicAppointmentCalendar({ slots = [], selected, onToggle, disabled = false }) {
  // Ancora iniziale: primo mese che contiene uno slot; fallback al mese corrente.
  const [anchor, setAnchor] = useState(() => {
    const first = slots
      .map((s) => new Date(s.start_at))
      .filter((d) => !Number.isNaN(d.getTime()))
      .sort((a, b) => a - b)[0];
    return startOfMonth(first || new Date());
  });
  const today = new Date();

  // Raggruppa gli slot per giorno.
  const slotsByDay = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      if (!s?.start_at) continue;
      const dt = new Date(s.start_at);
      if (Number.isNaN(dt.getTime())) continue;
      const k = dayKey(dt);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push({ ...s, dt });
    }
    for (const arr of map.values()) arr.sort((a, b) => a.dt - b.dt);
    return map;
  }, [slots]);

  const cells = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const monthLabel = `${MONTHS_IT[anchor.getMonth()]} ${anchor.getFullYear()}`;

  const [expandedDayKey, setExpandedDayKey] = useState(() => {
    // Espandi automaticamente il primo giorno del mese con slot
    const days = Array.from(slotsByDay.keys());
    return days[0] || null;
  });

  const goPrev = () => setAnchor((d) => addMonths(d, -1));
  const goNext = () => setAnchor((d) => addMonths(d, 1));
  const goToday = () => setAnchor(startOfMonth(new Date()));

  return (
    <div data-testid="public-appointment-calendar" className="w-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-1 py-2 sm:px-2 sm:py-2.5 border-b">
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            type="button"
            onClick={goPrev}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
            aria-label="Mese precedente"
            data-testid="public-cal-prev"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
          <button
            type="button"
            onClick={goNext}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
            aria-label="Mese successivo"
            data-testid="public-cal-next"
          >
            <CaretRight size={16} weight="bold" />
          </button>
          <button
            type="button"
            onClick={goToday}
            className="ml-1 text-[11px] sm:text-xs font-semibold px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-100 text-slate-700"
            data-testid="public-cal-today"
          >
            Oggi
          </button>
        </div>
        <div className="font-display text-sm sm:text-base font-semibold text-slate-900 capitalize" data-testid="public-cal-month">
          {monthLabel}
        </div>
        <div className="w-[68px] sm:w-[92px]" />
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 text-center text-[10px] sm:text-[11px] font-semibold text-slate-500 uppercase border-b">
        {WD_SHORT.map((w, i) => (
          <div key={w} className="py-1 sm:py-1.5">
            <span className="hidden sm:inline">{w}</span>
            <span className="sm:hidden">{WD_MIN[i]}</span>
          </div>
        ))}
      </div>

      {/* Griglia 7 x 6 */}
      <div className="grid grid-cols-7 grid-rows-6 auto-rows-fr border-b">
        {cells.map((d, idx) => {
          const inMonth = d.getMonth() === anchor.getMonth();
          const isToday = sameDay(d, today);
          const k = dayKey(d);
          const daySlots = slotsByDay.get(k) || [];
          const hasSlot = daySlots.length > 0;
          const hasSelected = daySlots.some((s) => selected?.has?.(s.id));
          const clickable = hasSlot && !disabled && inMonth;
          const isPast = d < new Date(today.getFullYear(), today.getMonth(), today.getDate()) && !hasSlot;

          return (
            <button
              type="button"
              key={idx}
              disabled={!clickable}
              onClick={() => setExpandedDayKey(k)}
              className={
                "relative min-h-[42px] sm:min-h-[64px] border-r border-b last:border-r-0 p-1 flex flex-col items-center justify-start text-center transition-colors " +
                (!inMonth
                  ? "bg-slate-50/70 text-slate-300"
                  : !hasSlot
                  ? (isPast ? "bg-slate-50 text-slate-300" : "bg-slate-50 text-slate-400")
                  : hasSelected
                  ? "bg-purple-100 hover:bg-purple-200 text-purple-900"
                  : "bg-white hover:bg-purple-50 text-slate-800") +
                (clickable ? " cursor-pointer" : " cursor-default") +
                (expandedDayKey === k && hasSlot ? " ring-2 ring-purple-500 ring-inset" : "")
              }
              data-testid={`public-cal-cell-${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`}
              data-has-slot={hasSlot ? "1" : "0"}
              aria-label={
                hasSlot
                  ? `${d.getDate()} ${MONTHS_IT[d.getMonth()]} — ${daySlots.length} orari disponibili`
                  : `${d.getDate()} ${MONTHS_IT[d.getMonth()]} — non disponibile`
              }
            >
              <span
                className={
                  "text-[11px] sm:text-xs font-semibold " +
                  (isToday && inMonth
                    ? "inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-purple-600 text-white"
                    : "")
                }
              >
                {d.getDate()}
              </span>
              {hasSlot && inMonth && (
                <>
                  {/* Pallino/i indicatori orari */}
                  <div className="mt-0.5 flex items-center justify-center gap-0.5 flex-wrap">
                    {daySlots.slice(0, 3).map((s) => (
                      <span
                        key={s.id}
                        className={
                          "inline-block w-1.5 h-1.5 rounded-full " +
                          (selected?.has?.(s.id) ? "bg-purple-600" : "bg-emerald-500")
                        }
                      />
                    ))}
                    {daySlots.length > 3 && (
                      <span className="text-[9px] font-bold text-slate-500">+{daySlots.length - 3}</span>
                    )}
                  </div>
                  <span className="hidden sm:block text-[9px] text-slate-500 mt-0.5">
                    {daySlots.length} {daySlots.length === 1 ? "orario" : "orari"}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Orari del giorno selezionato */}
      <div className="pt-3">
        {(() => {
          const list = (expandedDayKey && slotsByDay.get(expandedDayKey)) || [];
          if (list.length === 0) {
            return (
              <div className="text-center text-xs text-slate-500 italic py-4">
                Clicca su un giorno evidenziato per vedere gli orari disponibili.
              </div>
            );
          }
          const anyDate = list[0].dt;
          return (
            <div data-testid="public-cal-day-slots">
              <div className="text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                Orari disponibili — {anyDate.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {list.map((s) => {
                  const active = selected?.has?.(s.id);
                  return (
                    <button
                      type="button"
                      key={s.id}
                      disabled={disabled}
                      onClick={() => onToggle?.(s.id)}
                      className={
                        "border rounded-lg px-2 py-2 flex flex-col items-center justify-center text-center transition-all " +
                        (active
                          ? "bg-purple-50 border-purple-500 ring-1 ring-purple-500 text-purple-900"
                          : "bg-white border-slate-200 hover:border-purple-300 text-slate-800") +
                        (disabled ? " opacity-50 cursor-not-allowed" : " cursor-pointer")
                      }
                      data-testid={`public-slot-${s.id}`}
                    >
                      <div className="flex items-center gap-1">
                        <Clock size={12} className="text-purple-500 shrink-0" />
                        <span className="text-sm font-semibold">
                          {s.dt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {active && <CheckCircle size={13} weight="fill" className="text-purple-600" />}
                      </div>
                      <span className="text-[10px] text-slate-500 mt-0.5">{s.duration_min} min</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-3 text-[10px] sm:text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> Disponibile</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-purple-600" /> Selezionato</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-slate-300" /> Non disponibile</span>
      </div>
    </div>
  );
}
